/** Journey log kept on this phone. It resumes after a restart and does not invent times. */

export const LOG_FIELDS = ["offBlock", "takeoff", "landing", "onBlock"] as const;

export type LogField = (typeof LOG_FIELDS)[number];

export const LOG_FIELD_LABEL: Record<LogField, string> = {
  offBlock: "Off block",
  takeoff: "Takeoff",
  landing: "Landing",
  onBlock: "On block",
};

export type LogDraft = {
  tripId: string;
  callsign: string;
  route: string;
  offBlock: string;
  takeoff: string;
  landing: string;
  onBlock: string;
  confirmed: boolean;
  updatedAt: string;
};

export type SimTimes = {
  offBlock?: string;
  takeoff?: string;
  landing?: string;
  onBlock?: string;
};

const KEY = "ns.companion.logDrafts";

export function emptyLogDraft(input: { tripId: string; callsign: string; route: string }, now = new Date()): LogDraft {
  return {
    tripId: input.tripId,
    callsign: input.callsign,
    route: input.route,
    offBlock: "",
    takeoff: "",
    landing: "",
    onBlock: "",
    confirmed: false,
    updatedAt: now.toISOString(),
  };
}

export function nextLogField(field: LogField): LogField | null {
  const index = LOG_FIELDS.indexOf(field);
  return LOG_FIELDS[index + 1] ?? null;
}

function stamp(value: string): number | null {
  if (!value.trim()) return null;
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : null;
}

/** Empty string when the four block times are in order. Otherwise the first problem. */
export function logDraftError(draft: LogDraft): string {
  for (const field of LOG_FIELDS) {
    if (!draft[field].trim()) return `${LOG_FIELD_LABEL[field]} is required`;
    if (stamp(draft[field]) == null) return `${LOG_FIELD_LABEL[field]} is not a time`;
  }
  const order: LogField[] = ["offBlock", "takeoff", "landing", "onBlock"];
  for (let i = 1; i < order.length; i += 1) {
    const prev = stamp(draft[order[i - 1]]) ?? 0;
    const next = stamp(draft[order[i]]) ?? 0;
    if (next < prev) return `${LOG_FIELD_LABEL[order[i]]} is before ${LOG_FIELD_LABEL[order[i - 1]]}`;
  }
  return "";
}

export type LogCompleteness = "empty" | "partial" | "invalid" | "complete";

export function logCompleteness(draft: LogDraft): LogCompleteness {
  const filled = LOG_FIELDS.filter((field) => draft[field].trim());
  if (!filled.length) return "empty";
  if (filled.length < LOG_FIELDS.length) return "partial";
  return logDraftError(draft) ? "invalid" : "complete";
}

export function simTimesAvailable(sim: SimTimes | null | undefined): boolean {
  if (!sim) return false;
  return LOG_FIELDS.some((field) => Boolean(sim[field]?.trim()));
}

/** Copy only the sim fields that exist. Blank sim fields stay as the pilot left them. */
export function fillDraftFromSim(draft: LogDraft, sim: SimTimes, now = new Date()): LogDraft {
  const next = { ...draft, updatedAt: now.toISOString() };
  for (const field of LOG_FIELDS) {
    const value = sim[field]?.trim();
    if (value) next[field] = value;
  }
  return next;
}

export function loadLogDrafts(storage?: Pick<Storage, "getItem"> | null): LogDraft[] {
  try {
    const raw = storage?.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((row) => row && typeof row.tripId === "string") as LogDraft[];
  } catch {
    return [];
  }
}

export function saveLogDraft(draft: LogDraft, storage?: Pick<Storage, "getItem" | "setItem"> | null): LogDraft[] {
  const next = [draft, ...loadLogDrafts(storage).filter((row) => row.tripId !== draft.tripId)];
  storage?.setItem(KEY, JSON.stringify(next));
  return next;
}

export function draftNeedsResume(draft: LogDraft): boolean {
  return !draft.confirmed || logCompleteness(draft) !== "complete";
}
